import { useEffect, useState } from 'react';

const DEFAULT_ROOT_MARGIN = '200px';

const hasIntersectionObserver = () =>
    typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';

/**
 * Tells whether an element has entered (or is about to enter) the viewport.
 *
 * The result is sticky: once the element has been seen it stays `true`, which
 * is what deferred loading needs (we never want to unload what we just fetched).
 * When IntersectionObserver is unavailable the hook reports `true` immediately
 * so callers keep their previous eager behaviour.
 *
 * @param {React.RefObject<Element>} ref - Ref on the observed element
 * @param {Object} options
 * @param {string} options.rootMargin - Margin around the viewport (default: '200px')
 * @param {boolean} options.enabled - Set to false to skip observing (reports true)
 * @returns {boolean} - Whether the element is near the viewport
 */
function useNearViewport(ref, { rootMargin = DEFAULT_ROOT_MARGIN, enabled = true } = {}) {
    const [isNear, setIsNear] = useState(false);

    useEffect(() => {
        if (!enabled) return undefined;
        if (isNear) return undefined;

        const node = ref.current;
        if (!node || !hasIntersectionObserver()) {
            setIsNear(true);
            return undefined;
        }

        const observer = new window.IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setIsNear(true);
                    observer.disconnect();
                }
            },
            { rootMargin }
        );
        observer.observe(node);

        return () => observer.disconnect();
    }, [ref, rootMargin, enabled, isNear]);

    return !enabled || isNear;
}

export default useNearViewport;
